import _ from "lodash";
import type { FieldValues } from "react-hook-form";
import { selectorFamily } from "recoil";
import type { ObjectShape } from "yup";

import {
  createYupSchema,
  extractSearchableControlName,
  getComputedGridFieldName,
  getConditionFieldNames,
  getDynamicFormBulkUpdateControls,
  getDynamicFormControls,
  getDynamicGridControls,
  getSearchablePhoneFieldNames,
  getValidatedFormControls,
  isCustomFieldFilterCompatible,
  isCustomFieldReadableByPermission,
  isCustomFieldSearchable,
  isEntityServerSideFiltered,
  Log,
  unique,
} from "../../helpers";
import type {
  ColumnOrderState,
  VisibilityState,
} from "../../helpers/react-table.features";
import { manualAdminFilters } from "../../mappers";
import type {
  ICustomField,
  IEditFormTabContainer,
  IFormSchema,
  IGridSchema,
  RydeAdminUnit,
  RydeUiUnit,
} from "../../models";
import {
  dynamicFormTabIndexAF,
  entitiesAtom,
  fieldValuesAF,
  schemaRootAF,
  tableFiltersSchemaAtomFamily,
} from "../atoms";
import { profilePermissionsSelector } from "./profile.selectors";
import { timezoneSelector } from "./system-settings.selectors";

export const controlsSF = selectorFamily<ICustomField[], RydeUiUnit>({
  key: "customFieldsSF",
  get:
    entity =>
    ({ get }) => {
      const schemaRoot = get(schemaRootAF(entity));
      return schemaRoot?.controls || [];
    },
});
export const dynamicFormBulkUpdateControlsSF = selectorFamily<
  ICustomField[],
  RydeUiUnit
>({
  key: "dynamicFormBulkUpdateControlsSF",
  get:
    entity =>
    ({ get }) => {
      const controls = get(controlsSF(entity));
      const formSchema = get(formSchemaSF(entity));
      return getDynamicFormBulkUpdateControls(controls, formSchema);
    },
});
export const controlNameMapSF = selectorFamily<
  Map<string, ICustomField>,
  RydeUiUnit
>({
  key: "controlNameMapSF",
  get:
    entity =>
    ({ get }) => {
      const schemaRoot = get(schemaRootAF(entity));
      return (schemaRoot?.controls ?? []).reduce((acc, customField) => {
        return new Map(acc).set(customField.name, customField);
      }, new Map<string, ICustomField>());
    },
});

export const filterSearchableControlsSF = selectorFamily<
  ICustomField["name"][],
  RydeUiUnit
>({
  key: "filterSearchableControlsSF",
  get:
    entity =>
    ({ get }) => {
      const controls = get(controlsSF(entity));
      const schemaFields =
        controls
          ?.filter(isCustomFieldSearchable)
          .map(extractSearchableControlName) || [];

      const adminFilters = manualAdminFilters[entity as RydeAdminUnit] ?? [];
      if (!adminFilters.length) {
        return schemaFields;
      }
      const merged = unique([...schemaFields, ...adminFilters]);
      return merged;
    },
});

export const filterSearchablePhoneFieldNamesSF = selectorFamily<
  ICustomField["name"][],
  RydeUiUnit
>({
  key: "filterSearchablePhoneFieldNamesSF",
  get:
    entity =>
    ({ get }) =>
      getSearchablePhoneFieldNames(get(controlsSF(entity))),
});

export const controlsFilterCompatibleSF = selectorFamily<
  ICustomField[],
  RydeUiUnit
>({
  key: "filterClientCompatibleControlsSF",
  get:
    entity =>
    ({ get }) => {
      const isServerSideFilteredEntity = isEntityServerSideFiltered(entity);
      const gridControls = isServerSideFilteredEntity
        ? get(controlsSF(entity))
        : get(gridControlsSF(entity));
      return gridControls?.filter(isCustomFieldFilterCompatible) || [];
    },
});

export const availableToAddFiltersControlsSF = selectorFamily<
  ICustomField[],
  RydeUiUnit
>({
  key: "availableToAddFiltersControlsSF",
  get:
    entity =>
    ({ get }) => {
      const filterSchema = get(tableFiltersSchemaAtomFamily(entity)) ?? {};

      const compatibleControls = get(controlsFilterCompatibleSF(entity));
      return (
        compatibleControls?.filter(
          control => !(String(control.id) in filterSchema),
        ) || []
      );
    },
});

export const formSchemaSF = selectorFamily<IFormSchema, RydeUiUnit>({
  key: "formSchemaSF",
  get:
    entity =>
    ({ get }) => {
      const schemaRoot = get(schemaRootAF(entity));
      return schemaRoot?.formSchema || {};
    },
});

export const gridSchemaSF = selectorFamily<IGridSchema, RydeUiUnit>({
  key: "gridSchemaSF",
  get:
    entity =>
    ({ get }) => {
      const schemaRoot = get(schemaRootAF(entity));
      return schemaRoot?.gridSchema || {};
    },
});

export const defaultSchemaFiltersIdsSF = selectorFamily<number[], RydeUiUnit>({
  key: "defaultSchemaFiltersIdsSF",
  get:
    entity =>
    ({ get }) => {
      const schemaRoot = get(gridSchemaSF(entity));
      return schemaRoot?.defaultFilters || [];
    },
});

export const tabSF = selectorFamily<IEditFormTabContainer[], RydeUiUnit>({
  key: "tabSF",
  get:
    entity =>
    ({ get }) => {
      const formSchema = get(formSchemaSF(entity));
      return formSchema?.layout || [];
    },
});

export const formTabControlsSF = selectorFamily<ICustomField[], RydeUiUnit>({
  key: "formTabControlsSF",
  get:
    entity =>
    ({ get }) => {
      const controls = get(controlsSF(entity));
      const tabs = get(tabSF(entity));
      const tabIndex = get(dynamicFormTabIndexAF(entity));
      return getDynamicFormControls(controls, tabs, tabIndex);
    },
});

export const yupValidationSchemaSF = selectorFamily<ObjectShape, RydeUiUnit>({
  key: "yupValidationSchemaSF",
  get:
    entity =>
    ({ get }) => {
      const controls = get(controlsSF(entity));
      const formSchema = get(formSchemaSF(entity));
      const validatedControls = getValidatedFormControls(controls, formSchema);
      const fieldValues = get(fieldValuesAF(entity));
      // Date rules must resolve "today" in the company timezone, not the viewer's.
      const timezone = get(timezoneSelector);
      return createYupSchema(validatedControls, fieldValues, timezone);
    },
});

export const gridControlsSF = selectorFamily<ICustomField[], RydeUiUnit>({
  key: "gridControlsSF",
  get:
    entity =>
    ({ get }) => {
      const gridScheme = get(gridSchemaSF(entity));
      const controls = get(controlsSF(entity));
      const entities = get(entitiesAtom);
      const roleDataItem = get(profilePermissionsSelector);
      const entityIdToName = new Map(
        entities?.map(({ id, name }) => [id, name as RydeUiUnit]) ?? [],
      );
      const dynamicGridControls = getDynamicGridControls(
        controls,
        gridScheme?.columns || [],
      );

      return dynamicGridControls.filter(control =>
        isCustomFieldReadableByPermission({
          control,
          permissions: roleDataItem?.permissions,
          entityIdToName,
        }),
      );
    },
});

/**
 * Every field path that a visible or disable condition on this entity reads.
 *
 * The form snapshot behind fieldValuesProxySF exists only to resolve those
 * conditions, so a form needs to react to these values and nothing else — a
 * keystroke in an unrelated field cannot change which controls render, which
 * are disabled, or which are validated.
 */
export const conditionFieldNamesSF = selectorFamily<string[], RydeUiUnit>({
  key: "conditionFieldNamesSF",
  get:
    entity =>
    ({ get }) => {
      const controls = get(controlsSF(entity));
      const fieldNames = new Set<string>();

      controls.forEach(({ formVisibleCondition, formAttributes }) => {
        getConditionFieldNames(formVisibleCondition).forEach(name =>
          fieldNames.add(name),
        );
        getConditionFieldNames(formAttributes?.disableCondition).forEach(name =>
          fieldNames.add(name),
        );
      });

      return [...fieldNames];
    },
});

export const fieldValuesProxySF = selectorFamily<FieldValues, RydeUiUnit>({
  key: "fieldValuesProxySF",
  get:
    entity =>
    ({ get }) => {
      return get(fieldValuesAF(entity));
    },
  set:
    entity =>
    ({ get, set }, updatedFormValues) => {
      const prevVals = get(fieldValuesAF(entity));

      if (!_.isEqual(prevVals, updatedFormValues)) {
        Log.debug(
          "updating watched form data snapshot for value change, updated values:",
          updatedFormValues,
          " prev.values: ",
          prevVals,
        );
        set(fieldValuesAF(entity), updatedFormValues);
      }
    },
});

export const prohibitedToDuplicateFieldsSF = selectorFamily<
  ICustomField[],
  RydeUiUnit
>({
  key: "prohibitedToDuplicateFieldsSF",
  get:
    entity =>
    ({ get }) => {
      const controls = get(controlsSF(entity));

      return controls.filter(control =>
        Boolean(control?.formAttributes?.excludeOnDuplicate),
      );
    },
});

export const isSchemaRootLoadedSF = selectorFamily<boolean, RydeUiUnit>({
  key: "schemaRootAF",
  get:
    entity =>
    ({ get }) => {
      const schemaRoot = get(schemaRootAF(entity));

      return schemaRoot !== undefined;
    },
});

export const unpatchedVisibilityStateSF = selectorFamily<
  VisibilityState,
  RydeUiUnit
>({
  key: "unpatchedVisibilityStateSF",
  get:
    entity =>
    ({ get }) => {
      const gridControls = get(gridControlsSF(entity));
      const visibilityState = {} as VisibilityState;
      gridControls.forEach(f => {
        const colNameComputed = getComputedGridFieldName(f);
        visibilityState[colNameComputed] = false;
      });
      return visibilityState;
    },
});

export const columnsVisibilityGridSchemaStateSF = selectorFamily<
  VisibilityState,
  RydeUiUnit
>({
  key: "columnsVisibilityGridSchemaStateSF",
  get:
    entity =>
    ({ get }) => {
      const gridSchema = get(gridSchemaSF(entity));
      const unpatchedVisibilityState = get(unpatchedVisibilityStateSF(entity));
      const gridControls = get(gridControlsSF(entity));
      const visibilityState = {} as VisibilityState;
      Object.keys(gridSchema?.columnsVisibility ?? []).forEach(columnId => {
        const control = gridControls.find(
          c => Number(c.id) === Number(columnId),
        );

        if (!control) {
          return;
        }

        const colNameComputed = getComputedGridFieldName(control);
        visibilityState[colNameComputed] =
          gridSchema?.columnsVisibility?.[Number(columnId)] ?? false;
      });
      return { ...unpatchedVisibilityState, ...visibilityState };
    },
});

export const gridSchemaOrderSF = selectorFamily<ColumnOrderState, RydeUiUnit>({
  key: "gridSchemaOrderSF",
  get:
    entity =>
    ({ get }) => {
      const gridSchema = get(gridSchemaSF(entity));
      const gridControls = get(gridControlsSF(entity));
      const columnOrderState = [] as ColumnOrderState;
      gridSchema.columnsOrder?.forEach(controlId => {
        const control = gridControls.find(c => c.id === controlId);

        if (!control) {
          return;
        }

        const colNameComputed = getComputedGridFieldName(control);
        columnOrderState.push(colNameComputed);
      });
      return columnOrderState;
    },
});
